import { MoreHorizontal, Plus, Search, Tag } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { apiUrl } from '@/config/api';

const API_BASE = apiUrl('/api/fees');

interface Fee {
  id: string;
  code: string;
  name: string;
  type: string;
  amount: number;
  applicantCategory: string;
}

export default function FeeList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fees, setFees] = useState<Fee[]>([]);

  useEffect(() => {
    const fetchFees = async () => {
      try {
        setLoading(true);
        const res = await fetch(API_BASE);
        if (res.ok) {
          const data = await res.json();
          setFees(data as Fee[]);
        }
      } catch (error) {
        console.error('Failed to fetch fees', error);
      } finally {
        setLoading(false);
      }
    };
    fetchFees();
  }, []);

  const getPriceColor = (type: string) => {
    switch (type) {
      case 'EXAM_FEE':
        return 'text-blue-600';
      case 'ADMISSION_FEE':
        return 'text-purple-600';
      default:
        return 'text-gray-900';
    }
  };

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900">納付金マスタ</h1>
          <p className="text-gray-500 mt-1">検定料、入学金、授業料などの請求項目を管理します。</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline">一括アップロード</Button>
          <Button
            className="bg-indigo-600 hover:bg-indigo-700 shadow-md"
            onClick={() => navigate('/admin/enrollment/fees/new')}
          >
            <Plus className="w-4 h-4 mr-2" /> 新規アイテム追加
          </Button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex gap-2">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
          <Input placeholder="商品名・コードで検索..." className="pl-9" />
        </div>
      </div>

      {/* Table */}
      <Card className="border-t-4 border-t-indigo-500 shadow-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>商品コード</TableHead>
                <TableHead>商品名</TableHead>
                <TableHead>区分</TableHead>
                <TableHead>金額 (税込)</TableHead>
                <TableHead>対象者</TableHead>
                <TableHead className="text-right">{t('common.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableBody>
                {loading && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-10 text-gray-400">
                      読み込み中...
                    </TableCell>
                  </TableRow>
                )}
                {!loading && fees.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-10 text-gray-400">
                      料金項目が登録されていません。
                    </TableCell>
                  </TableRow>
                )}
                {!loading &&
                  fees.length > 0 &&
                  fees.map((fee) => (
                    <TableRow key={fee.id} className="group hover:bg-gray-50">
                      <TableCell className="font-mono text-xs text-gray-500">{fee.code}</TableCell>
                      <TableCell>
                        <div className="font-medium text-gray-900">{fee.name}</div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary" className="font-normal capitalize">
                          {fee.type.replace('_', ' ').toLowerCase()}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className={`font-bold ${getPriceColor(fee.type)}`}>¥{fee.amount?.toLocaleString()}</div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-sm text-gray-600">
                          <Tag className="w-3 h-3" />
                          {fee.applicantCategory}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuLabel>{t('common.actions')}</DropdownMenuLabel>
                            <DropdownMenuItem onClick={() => navigate(`/admin/enrollment/fees/${fee.id}`)}>
                              編集
                            </DropdownMenuItem>
                            <DropdownMenuItem className="text-red-600">削除</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
